<template>
  <lw-overlay
    v-if="!estimatesLoading && !ratesLoading"
    :size="'c1'"
    :colour="'grey-300'"
  >
    <header class="grid mb1">
      <h1 class="order-1 order-0_sm">Estimate settings</h1>
      <div class="align-right order-0 order-1_sm"><button
        class="invisible-button"
        @click.prevent="duplicate"><svg-duplicate/> Make a copy</button></div>
    </header>
    <form
      class="grid grid-columns-2_sm"
      @submit.prevent="estimateSettings">
      <fieldset class="subtle p2">
        <h2>Estimate details</h2>
        <hr class="mb2 c1">
        <lw-text-input
          v-validate="'required'"
          :label="'Client'"
          :name="'client'"
          :type="'text'"
          :error="errors.first('client')"
          v-model="client"
        />
        <lw-text-input
          v-validate="'required'"
          :label="'Project name'"
          :name="'project'"
          :type="'text'"
          v-model="project"
          :error="errors.first('project')"
        />
        <lw-text-area
          :name="'description'"
          :label="'Description'"
          :optional="true"
          v-model="description"
        />
      </fieldset>
      <fieldset class="subtle p2">
        <h2>Estimate rates</h2>
        <hr class="mb2">
        <div class="">

          <div class="order-2 order-1_sm">
            <lw-select-input
              v-if="rateList.length"
              :label="'Rate card'"
              :name="'rateCard'"
              :id="'rateCard'"
              :options="rateList"
              :placeholder="'Don’t use rate card'"
              v-model="rateCard"
              @input="updateRate"
            />
            <div
              v-else
              class="mr1">
              <p class="font-size-3 bold">Rate card</p>
              <p class="font-size-1">No rate cards setup. <router-link :to="{ name: 'rates'}">Add one.</router-link></p>
            </div>
          </div>
          <div class="order-1 order-2_sm">
            <div
              v-if="rateCard !== 'zzzzz'"
              class="mb2">
              <span class="price inline-block font-size-8 extra-condensed">{{ ratePreview | currency }}</span><span class="font-size-2 inline-block">/{{ label | lowercase }}</span>
            </div>
            <div
              v-else
              class="flex align-items-center">
              <lw-text-input
                :label="'Manual rate'"
                :name="'rateValue'"
                :type="'number'"
                :align="'right'"
                :class="'inline-block c0'"
                v-model.number="ratePreview"
              />
              <span class="font-size-2 inline-block  mt1px">/{{ label | lowercase }}</span>
            </div>
          </div>
          <lw-button-group
            :name="'rateType'"
            :current="rateType"
            :label="'Estimating units'"
            :options="rates"
            :class="'my0 py0 pr1 c1 units grid-column-span-3_sm order-3'"
            v-model.number="rateType"
            @input="updateRate"
          />
          <p class="font-size-1 line-height-1 dark-red-text order-4 grid-column-span-2_sm"><strong>Warning:</strong> Changing units will impact your estimate. Make sure to review your tasks since they adopt the original unit duration. E.g., Changing from days to weeks: 2 days becomes 2 weeks.</p>
        </div>
      </fieldset>
      <div class="grid-column-full mt1 mb-margin">
        <button
          v-shortkey="['meta', 's']"
          class="accent-button large-button full-button_mb mr1"
          type="submit"
          @shortkey="estimateSettings">Save</button>
        <button
          class="invisible-button negative-button"
          @click.prevent="close">Cancel</button>
        <button
          class="invisible-button negative-button right"
          @click.prevent="isConfirming = true"><svg-archive/> Archive estimate</button>
      </div>
    </form>
    <app-archive-estimate
      v-if="isConfirming"
      :name="estimate.project"
      :page="page"
      :is-confirming.sync="isConfirming"/>
  </lw-overlay>
</template>

<script>
import { mapGetters } from 'vuex'
import TextInput from '../livewires/TextInput'
import TextArea from '../livewires/TextArea'
import Overlay from '../livewires/Overlay'
import SelectInput from '../livewires/SelectInput'
import ButtonGroup from '../livewires/ButtonGroup'
import ArchiveEstimate from './ArchiveEstimate'
import Duplicate from '../svgs/Duplicate'
import Archive from '../svgs/Archive'

export default {
  components: {
    lwTextInput: TextInput,
    lwTextArea: TextArea,
    lwSelectInput: SelectInput,
    lwButtonGroup: ButtonGroup,
    lwOverlay: Overlay,
    appArchiveEstimate: ArchiveEstimate,
    svgDuplicate: Duplicate,
    svgArchive: Archive
  },
  data () {
    return {
      page: this.$route.params.id,
      project: '',
      client: '',
      description: '',
      mountedRateCard: false,
      isConfirming: false,
      ratePreview: '',
      rateType: '',
      rateCard: 'zzzzz',
      label: ''
    }
  },
  computed: {
    ...mapGetters([
      'rateList',
      'estimatesLoading',
      'ratesLoading',
      'user'
    ]),
    estimate () {
      return this.$store.getters.estimate(this.page)
    },
    settings () {
      return this.$store.getters.estimateSettings(this.page)
    },
    rates () {
      return this.$store.getters.rateUnits(this.rateCard)
    }
  },
  mounted () {
    this.mountedRateCard = this.estimate.rateCard
    this.project = this.estimate.project
    this.client = this.estimate.client
    this.description = this.estimate.description
    this.estimate.rateCard === false ? this.rateCard = 'zzzzz' : this.rateCard = this.estimate.rateCard
    this.ratePreview = this.estimate.rateValue
    this.rateType = this.estimate.rateType
    this.label = this.rates[this.rateType].label
  },
  methods: {
    estimateSettings () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$store.dispatch('saveState', false)
          let payload = { ...this.estimate }
          payload.rateType = this.rateType
          payload.id = this.page
          payload.project = this.project
          payload.client = this.client
          payload.description = this.description
          if (this.mountedRateCard !== payload.rateCard) {
            payload.lastRateCard = this.mountedRateCard
          }
          payload.rateCard = 'zzzzz'
          if (this.rateCard !== 'zzzzz') {
            this.mountedRateCard = this.estimate.rateCard
            payload.rateCard = this.rateCard
            payload.rateValue = this.rates[this.rateType].amount
          } else {
            this.mountedRateCard = 'zzzzz'
            payload.rateValue = this.ratePreview
          }
          this.$store.dispatch('estimateSettings', payload).then(response => {
            this.$router.push({ name: 'estimate', params: { id: this.page } })
            this.$emit('estimateSettings', 'updated')
          })
        }
      })
    },
    updateRate () {
      this.label = this.rates[this.rateType].label
      if (this.rateCard !== 'zzzzz') {
        this.ratePreview = this.rates[this.rateType].amount
      } else {
        this.ratePreview = this.estimate.rateValue
      }
    },
    close () {
      this.$router.push({ name: 'estimate', params: { id: this.page } })
    },
    duplicate () {
      this.$store.dispatch('toggleLoading', true)
      this.$store.dispatch('saveState', false)
      const payload = { ...this.estimate }
      payload.owner = this.user.id
      payload.id = this.page
      this.$store.dispatch('duplicateEstimate', payload).then(response => {
        this.$router.push({ name: 'estimates' })
      })
    }
  }
}
</script>

<style scoped>
@media screen and (min-width: 48em) {
  header {
    grid-template-columns: auto auto;
    align-items: baseline;
  }
}
</style>
